import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { ref, watch, nextTick, onMounted, onBeforeUnmount } from "vue";
import SafeHtml from "@/components/ui/SafeHtml.vue";
import Lightbox from "@/components/ui/Lightbox.vue";
import FilePreview from "@/components/ui/FilePreview.vue";
import type { PreviewAttachment } from "@/components/ui";


export default /*@__PURE__*/_defineComponent({
  __name: 'NotePreview',
  props: {
    html: {}
  },
  setup(__props: any) {

const props = __props;

const host = ref<HTMLElement | null>(null);
const lightboxOpen = ref(false);
const startIndex = ref(0);
const images = ref<string[]>([]);
const filePreviewOpen = ref(false);
const filePreviewAttachment = ref<PreviewAttachment | null>(null);
let clickHandler: ((e: Event) => void) | null = null;

const EXT_MIME: Record<string, string> = {
  pdf: "application/pdf",
  txt: "text/plain",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  csv: "text/csv",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  zip: "application/zip",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  svg: "image/svg+xml",
  mp3: "audio/mpeg",
  mp4: "video/mp4",
  mov: "video/quicktime",
  webm: "video/webm",
};

function humanSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n >= 10 || i === 0 ? Math.round(n) : n.toFixed(1)} ${units[i]}`;
}

function variantFor(mime: string, name: string): string {
  const ext = (name.split(".").pop() || "").toLowerCase();
  if (mime === "application/pdf" || ext === "pdf") return "pdf";
  if (/^image\//.test(mime) || /^(png|jpe?g|gif|webp|svg|bmp)$/.test(ext)) return "img";
  if (/^video\//.test(mime) || /^(mp4|webm|mov|avi|mkv)$/.test(ext)) return "vid";
  if (/^audio\//.test(mime) || /^(mp3|wav|ogg|m4a|aac|flac)$/.test(ext)) return "aud";
  if (/zip|compressed/.test(mime) || /^(zip|7z|rar|tar|gz)$/.test(ext)) return "zip";
  if (/(word|document)/.test(mime) || /^(docx?|rtf)$/.test(ext)) return "doc";
  if (/(sheet|excel)/.test(mime) || /^(xlsx?|csv)$/.test(ext)) return "xls";
  return "file";
}

function iconSvg(variant: string): string {
  if (variant === "aud") {
    return `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>`;
  }
  const base = `<path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/>`;
  let inner = "";
  if (variant === "pdf" || variant === "doc") {
    inner = `<line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><line x1="10" y1="9" x2="9" y2="9"/>`;
  } else if (variant === "xls") {
    inner = `<line x1="8" y1="13" x2="10" y2="13"/><line x1="14" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="10" y2="17"/><line x1="14" y1="17" x2="16" y2="17"/>`;
  } else if (variant === "zip") {
    inner = `<line x1="10" y1="13" x2="10" y2="13.01"/><line x1="10" y1="15" x2="10" y2="15.01"/><line x1="10" y1="17" x2="10" y2="17.01"/>`;
  } else if (variant === "img") {
    inner = `<circle cx="10" cy="13" r="1.5"/><path d="m20 17-3-3-5 5"/>`;
  } else if (variant === "vid") {
    inner = `<polygon points="10 12 16 15 10 18"/>`;
  }
  return `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${base}${inner}</svg>`;
}

function looksLikeAttachmentLink(a: HTMLAnchorElement): boolean {
  if (a.classList.contains("rte-file-chip")) return true;
  if (a.hasAttribute("data-mime") || a.hasAttribute("data-name")) return true;
  if (a.hasAttribute("download")) return true;
  return false;
}

function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!
  );
}

function rebuildChip(a: HTMLAnchorElement): void {
  const href = a.getAttribute("href") || "";
  let fileName =
    a.getAttribute("data-name") || a.getAttribute("download") || a.textContent?.trim() || "File";
  // legacy chips may have collapsed name+size into the visible text — strip a trailing size
  fileName = fileName.replace(/\s*\d+(?:\.\d+)?\s*(B|KB|MB|GB)\s*$/i, "").trim() || fileName;
  // also strip if the filename incorrectly ends with a size token glued on
  let mime = a.getAttribute("data-mime") || "";
  if (!mime) {
    try {
      const ext = (
        new URL(href, window.location.origin).pathname.split(".").pop() || ""
      ).toLowerCase();
      mime = EXT_MIME[ext] || EXT_MIME[fileName.split(".").pop()?.toLowerCase() || ""] || "";
    } catch {
      mime = EXT_MIME[fileName.split(".").pop()?.toLowerCase() || ""] || "";
    }
  }
  const sizeAttr = Number(a.getAttribute("data-size")) || 0;
  const sizeText = humanSize(sizeAttr);
  const variant = variantFor(mime, fileName);

  a.classList.add("rte-file-chip");
  if (!a.hasAttribute("target")) a.setAttribute("target", "_blank");
  if (!a.hasAttribute("rel")) a.setAttribute("rel", "noopener noreferrer");
  if (!a.hasAttribute("data-name")) a.setAttribute("data-name", fileName);
  if (mime && !a.hasAttribute("data-mime")) a.setAttribute("data-mime", mime);
  // strip legacy inline color/underline so chip styles can take over
  a.style.color = "";
  a.style.textDecoration = "";

  a.innerHTML =
    `<span class="rte-file-icon rte-file-icon--${variant}">${iconSvg(variant)}</span>` +
    `<span class="rte-file-chip__body">` +
    `<span class="rte-file-chip__name">${escapeHtml(fileName)}</span>` +
    (sizeText ? `<span class="rte-file-chip__meta">${escapeHtml(sizeText)}</span>` : "") +
    `</span>`;
}

const enhance = () => {
  const root = host.value;
  if (!root) return;

  const imgs = Array.from(root.querySelectorAll<HTMLImageElement>("img"));
  const newSrcs = imgs.map((i) => i.src);
  if (newSrcs.join("|") !== images.value.join("|")) {
    images.value = newSrcs;
  }
  imgs.forEach((img, idx) => {
    if (!img.hasAttribute("loading")) img.setAttribute("loading", "lazy");
    if (!img.hasAttribute("decoding")) img.setAttribute("decoding", "async");
    if (img.style.cursor !== "zoom-in") img.style.cursor = "zoom-in";
    const nextIdx = String(idx);
    if (img.dataset.nplbIndex !== nextIdx) img.dataset.nplbIndex = nextIdx;
  });
  Array.from(root.querySelectorAll<HTMLVideoElement>("video")).forEach((v) => {
    v.setAttribute("preload", "metadata");
    v.setAttribute("controls", "controls");
    v.setAttribute("playsinline", "true");
  });
  Array.from(root.querySelectorAll<HTMLAudioElement>("audio")).forEach((a) => {
    a.setAttribute("preload", "metadata");
    a.setAttribute("controls", "controls");
  });

  Array.from(root.querySelectorAll<HTMLAnchorElement>("a[href]")).forEach((a) => {
    if (!looksLikeAttachmentLink(a)) return;
    // skip if already rebuilt by us
    if (a.querySelector(".rte-file-chip__body") && a.querySelector(".rte-file-icon")) return;
    rebuildChip(a);
  });
};

const onClick = (e: Event) => {
  const target = e.target as HTMLElement;

  const chip = target.closest("a.rte-file-chip") as HTMLAnchorElement | null;
  if (chip) {
    e.preventDefault();
    e.stopPropagation();
    const url = chip.getAttribute("href") || "";
    if (!url) return;
    filePreviewAttachment.value = {
      id: url,
      fileName: chip.getAttribute("data-name") || chip.getAttribute("download") || "File",
      mimeType: chip.getAttribute("data-mime") || "",
      fileSize: Number(chip.getAttribute("data-size")) || undefined,
      url,
    };
    filePreviewOpen.value = true;
    return;
  }

  if (target.tagName === "IMG" && target.dataset.nplbIndex != null) {
    e.preventDefault();
    startIndex.value = Number(target.dataset.nplbIndex);
    lightboxOpen.value = true;
  }
};

const close = () => {
  lightboxOpen.value = false;
};

watch(
  () => props.html,
  async () => {
    await nextTick();
    enhance();
  }
);

onMounted(() => {
  enhance();
  clickHandler = onClick;
  host.value?.addEventListener("click", clickHandler);
});

onBeforeUnmount(() => {
  if (clickHandler && host.value) {
    host.value.removeEventListener("click", clickHandler);
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "note-preview",
    ref_key: "host",
    ref: host
  }, [
    _createVNode(SafeHtml, { html: __props.html }, null, 8, ["html"]),
    _createVNode(Lightbox, {
      show: lightboxOpen.value,
      images: images.value,
      "start-index": startIndex.value,
      onClose: close
    }, null, 8, ["show", "images", "start-index"]),
    _createVNode(FilePreview, {
      show: filePreviewOpen.value,
      attachment: filePreviewAttachment.value,
      zIndex: 10080,
      onClose: _cache[0] || (_cache[0] = ($event: any) => (filePreviewOpen.value = false))
    }, null, 8, ["show", "attachment"])
  ], 512))
}
}

})